// @ts-check
import { initSchema } from '@aws-amplify/datastore';
import { schema } from './schema';

const AttachmentType = {
  "IMAGE": "IMAGE",
  "VIDEO": "VIDEO"
};

const BidStatus = {
  "AWARDED": "AWARDED",
  "REJECTED": "REJECTED",
  "CANCELLED": "CANCELLED"
};

const DocumentStatus = {
  "DRAFT": "DRAFT",
  "SUBMITTED": "SUBMITTED",
  "ACCEPTED": "ACCEPTED",
  "REJECTED": "REJECTED"
};

const DesignDocumentStatus = {
  "AUTOSAVE_DRAFT": "AUTOSAVE_DRAFT",
  "DRAFT": "DRAFT",
  "PUBLISHED": "PUBLISHED",
  "APPROVED": "APPROVED"
};

const DimensionType = {
  "LINEAR": "LINEAR",
  "SQUARE": "SQUARE"
};

const PermissionType = {
  "VIEW": "VIEW",
  "EDIT": "EDIT",
  "DELETE": "DELETE",
  "CREATE_CHILDREN": "CREATE_CHILDREN",
  "MANAGE_STATE": "MANAGE_STATE",
  "SHARE_PERMISSION": "SHARE_PERMISSION",
  "REMOVE_PERMISSION": "REMOVE_PERMISSION"
};

const PermissionEntityType = {
  "USER": "USER",
  "TEAM": "TEAM"
};

const PermissionResourceType = {
  "PROPERTY": "PROPERTY",
  "LOCATION": "LOCATION",
  "ISSUE": "ISSUE",
  "DIMENSION": "DIMENSION",
  "WORK_SPECIFICATION": "WORK_SPECIFICATION",
  "ATTACHMENT": "ATTACHMENT",
  "QUOTE": "QUOTE",
  "SOLUTION": "SOLUTION",
  "PROPOSAL": "PROPOSAL",
  "ASSESSMENT": "ASSESSMENT",
  "POLICY_TEMPLATE_LINK": "POLICY_TEMPLATE_LINK",
  "TEAM": "TEAM",
  "TEAM_MEMBER": "TEAM_MEMBER"
};

const InspectionStatus = {
  "IN_PROGRESS": "IN_PROGRESS",
  "PAUSED": "PAUSED",
  "COMPLETED": "COMPLETED",
  "ABANDONED": "ABANDONED"
};

const IssuePriority = {
  "HIGH": "HIGH",
  "MEDIUM": "MEDIUM",
  "LOW": "LOW"
};

const QuickQuoteStatus = {
  "STARTED": "STARTED",
  "PENDING_REVIEW": "PENDING_REVIEW",
  "REVIEWED": "REVIEWED",
  "COMPLETED": "COMPLETED"
};

const IssueStatus = {
  "SUBMITTED": "SUBMITTED",
  "ARCHIVED": "ARCHIVED",
  "APPROVED": "APPROVED",
  "ON_HOLD": "ON_HOLD",
  "COMPLETED": "COMPLETED",
  "IN_PROGRESS": "IN_PROGRESS",
  "DESIGN_PENDING": "DESIGN_PENDING",
  "DESIGNED": "DESIGNED",
  "SCHEDULED": "SCHEDULED"
};

const LogSeverityLevel = {
  "DEBUG": "DEBUG",
  "INFO": "INFO",
  "WARN": "WARN",
  "ERROR": "ERROR"
};

const MeasurementUnit = {
  "METER": "METER",
  "CENTIMETER": "CENTIMETER",
  "FOOT": "FOOT",
  "INCH": "INCH"
};

const OrganizationType = {
  "CONSULTANT": "CONSULTANT",
  "PROPERTY_MANAGEMENT": "PROPERTY_MANAGEMENT",
  "SOLE_SOURCE": "SOLE_SOURCE",
  "VENDOR": "VENDOR"
};

const TeamType = {
  "OWNER": "OWNER",
  "MEMBER": "MEMBER"
};

const AccountInvitationStatus = {
  "PENDING": "PENDING",
  "ACCEPTED": "ACCEPTED"
};

const BillingProvider = {
  "STRIPE": "STRIPE"
};

const ProposalStatus = {
  "DRAFT": "DRAFT",
  "SUBMITTED": "SUBMITTED"
};

const ResourceType = {
  "PROPERTY": "PROPERTY",
  "PROPOSAL": "PROPOSAL",
  "SOLUTION": "SOLUTION",
  "SOLUTION_MINOR_VERSION": "SOLUTION_MINOR_VERSION",
  "WORKTYPE": "WORKTYPE",
  "EXTERNAL_REPORT": "EXTERNAL_REPORT",
  "TENDER": "TENDER",
  "TENDER_MINOR_VERSION": "TENDER_MINOR_VERSION",
  "BID": "BID",
  "BID_MINOR_VERSION": "BID_MINOR_VERSION"
};

const SolutionTenderingType = {
  "SOLE_SOURCE": "SOLE_SOURCE",
  "TENDERED": "TENDERED"
};

const SolutionStatus = {
  "NEW_DOCUMENT": "NEW_DOCUMENT",
  "DRAFT": "DRAFT",
  "PENDING_REVIEW": "PENDING_REVIEW",
  "APPROVED": "APPROVED",
  "CANCELED": "CANCELED"
};

const UserOrganizationAssociationType = {
  "MEMBER_OF": "MEMBER_OF"
};

const UserOrganizationRole = {
  "ESTIMATOR": "ESTIMATOR",
  "ENGINEER": "ENGINEER",
  "PROPERTY_MANAGER": "PROPERTY_MANAGER",
  "OWNER": "OWNER"
};

const WorkTypeAssociationScopeEntity = {
  "PROPERTY": "PROPERTY",
  "USER": "USER",
  "ORGANIZATION": "ORGANIZATION"
};

const TenderStatus = {
  "PUBLISHED": "PUBLISHED",
  "AWARDED": "AWARDED",
  "COMPLETE": "COMPLETE",
  "CANCELLED": "CANCELLED",
  "CLOSED": "CLOSED"
};

const TemplateType = {
  "SOLUTION": "SOLUTION"
};

const TemplateEntityType = {
  "USER": "USER",
  "TEAM": "TEAM"
};

const SolutionOperationType = {
  "SAVE": "SAVE",
  "PUBLISH": "PUBLISH"
};

const ResponseStatus = {
  "SUCCESS": "SUCCESS",
  "ERROR": "ERROR"
};

const CompleteEventStatus = {
  "SUCCESS": "SUCCESS",
  "ERROR": "ERROR"
};

const LinkAction = {
  "ISSUE_FORM": "ISSUE_FORM",
  "PROPERTY_FORM": "PROPERTY_FORM",
  "ASSESSMENT_FORM": "ASSESSMENT_FORM",
  "EXTERNAL_REPORT": "EXTERNAL_REPORT",
  "CUSTOM_LINK": "CUSTOM_LINK",
  "UNASSIGNED": "UNASSIGNED"
};

const MeetingStatus = {
  "ACTIVE": "ACTIVE",
  "ENDED": "ENDED"
};

const AttendeeRole = {
  "INSPECTOR": "INSPECTOR",
  "AGENT": "AGENT"
};

const BuildStatus = {
  "IN_PROGRESS": "IN_PROGRESS",
  "SUCCEEDED": "SUCCEEDED",
  "FAILED": "FAILED",
  "STOPPED": "STOPPED"
};

const NotificationResourceType = {
  "PROPERTY": "PROPERTY",
  "ASSESSMENT_SCHEDULE": "ASSESSMENT_SCHEDULE"
};

const NotificationType = {
  "MISSED_ASSESSMENT": "MISSED_ASSESSMENT",
  "EXTERNAL_SUBMISSION": "EXTERNAL_SUBMISSION"
};

const NotificationRecipientIdType = {
  "CONTACT": "CONTACT",
  "USER": "USER"
};

const RecurrenceType = {
  "DAILY": "DAILY",
  "WEEKLY": "WEEKLY",
  "MONTHLY": "MONTHLY"
};

const DayOfWeek = {
  "SUNDAY": "SUNDAY",
  "MONDAY": "MONDAY",
  "TUESDAY": "TUESDAY",
  "WEDNESDAY": "WEDNESDAY",
  "THURSDAY": "THURSDAY",
  "FRIDAY": "FRIDAY",
  "SATURDAY": "SATURDAY"
};

const MonthlyScheduleType = {
  "DAY_OF_MONTH": "DAY_OF_MONTH",
  "NTH_WEEK_DAY": "NTH_WEEK_DAY"
};

const AssessmentStatus = {
  "NO_ISSUES": "NO_ISSUES",
  "ISSUES_FOUND": "ISSUES_FOUND"
};

const ExternalReportType = {
  "ISSUE": "ISSUE",
  "ASSESSMENT": "ASSESSMENT"
};

const ImageSource = {
  "CAMERA": "CAMERA",
  "GALLERY": "GALLERY"
};

const GenerateReportResourceType = {
  "SOLE_SOURCE": "SOLE_SOURCE",
  "BID": "BID",
  "TENDER_SUMMARY": "TENDER_SUMMARY"
};

const S3DownloadLocation = {
  "LOGO": "LOGO"
};

const NotificationChannel = {
  "EMAIL": "EMAIL",
  "SMS": "SMS"
};

const AccountClaimStatus = {
  "CLAIMED": "CLAIMED",
  "UNCLAIMED": "UNCLAIMED"
};

const MeetingLinkType = {
  "INSPECTOR": "INSPECTOR",
  "AGENT": "AGENT"
};

const AssessmentCompletionStatus = {
  "COMPLETED": "COMPLETED",
  "INCOMPLETE": "INCOMPLETE"
};

const AttachmentLocation = {
  "DEFAULT": "DEFAULT",
  "EXTERNAL_SUBMISSION": "EXTERNAL_SUBMISSION"
};

const AccountTier = {
  "DEVELOPER": "DEVELOPER",
  "STARTER": "STARTER",
  "STANDARD": "STANDARD",
  "PREMIUM": "PREMIUM",
  "ENTERPRISE": "ENTERPRISE"
};

const ConversationStatus = {
  "IN_PROGRESS": "IN_PROGRESS",
  "DONE": "DONE"
};

const ConversationNextStep = {
  "FOLLOW_UP": "FOLLOW_UP",
  "EXIT": "EXIT",
  "ESCALATE": "ESCALATE"
};

const GenerateIssuesFromImagesJobStatus = {
  "IN_PROGRESS": "IN_PROGRESS",
  "DONE": "DONE"
};

const { Attachment, BiddingContact, BidInvitation, Bid, BidMinorVersion, ClientLog, Counter, DesignDocument, DesignDocumentGroup, Dimension, DiscoverableWorkTypes, EntityResourcePermission, EntityWorkTypeAssociation, Inspection, Issue, QuickQuote, Location, Note, Organization, OrganizationBillingProfile, Team, TeamMember, AccountInvitation, Project, ProjectCounter, Property, PropertyCounter, Proposal, ProposalCounter, ResourceUserPermissions, Solution, SolutionMinorVersion, SolutionGroupCounter, UserGroup, UserGroupMember, UserOrganizationAssociation, WorkSpecification, WorkType, WorkTypeGroup, WorkTypePricing, DefaultWorkTypePricing, TemporaryWorkType, Tender, TenderMinorVersion, Tag, UserOnboardingProgress, User, Template, TemporaryAuthorizationCode, LinkRegistry, ExternalReportBranding, Meeting, Attendee, CodeBuildJob, ResourceNotificationSetting, AssessmentSchedule, ResourceAssessmentScheduleAssociation, Assessment, Contact, Property3PIntegrationConfiguration, ConversationState, PolicyTemplateLink, GenerateIssuesFromImagesJob, BidMetadata, ProposalDocLink, SolutionMetadata, Measurement, TenderMetadata, CreateTemporaryAuthorizationTokenResponse, ListResourceAggregateUserPermissionsOutput, AggregateUserPermissions, CompleteEvent, PlainFormConfiguration, AssessmentFormConfiguration, IssueSubmissionFormConfiguration, NotificationRecipient, Recurrence, DailyRecurrence, WeeklyRecurrence, MonthlyRecurrence, NthWeekDay, ExternalUserInfo, GetAccountClaimStatusResponse, ChimeMeetingOutput, SearchUsersOutput, SearchOrganizationsOutput, SearchableContactFilterOutput, ListFormConfigurationsByPropertyOutput, AssessmentScheduleByAssessors, ListAssessmentSchedulesByAssessorsOutput, SummarizeIssueOutput, FilterImagesOutput, WorkTypeDTO, SubmitQuickQuoteOutput, CreateBillingProfileSessionResponse, CreateBillingProfileResponse, SubscriptionDetails, ReportMeteringUsageResponse, UpdatesubscriptionTierResponse, ConversationStateOutput, ClassifyIssuesOutput, ClassifiedIssue } = initSchema(schema);

export {
  Attachment,
  BiddingContact,
  BidInvitation,
  Bid,
  BidMinorVersion,
  ClientLog,
  Counter,
  DesignDocument,
  DesignDocumentGroup,
  Dimension,
  DiscoverableWorkTypes,
  EntityResourcePermission,
  EntityWorkTypeAssociation,
  Inspection,
  Issue,
  QuickQuote,
  Location,
  Note,
  Organization,
  OrganizationBillingProfile,
  Team,
  TeamMember,
  AccountInvitation,
  Project,
  ProjectCounter,
  Property,
  PropertyCounter,
  Proposal,
  ProposalCounter,
  ResourceUserPermissions,
  Solution,
  SolutionMinorVersion,
  SolutionGroupCounter,
  UserGroup,
  UserGroupMember,
  UserOrganizationAssociation,
  WorkSpecification,
  WorkType,
  WorkTypeGroup,
  WorkTypePricing,
  DefaultWorkTypePricing,
  TemporaryWorkType,
  Tender,
  TenderMinorVersion,
  Tag,
  UserOnboardingProgress,
  User,
  Template,
  TemporaryAuthorizationCode,
  LinkRegistry,
  ExternalReportBranding,
  Meeting,
  Attendee,
  CodeBuildJob,
  ResourceNotificationSetting,
  AssessmentSchedule,
  ResourceAssessmentScheduleAssociation,
  Assessment,
  Contact,
  Property3PIntegrationConfiguration,
  ConversationState,
  PolicyTemplateLink,
  GenerateIssuesFromImagesJob,
  AttachmentType,
  BidStatus,
  DocumentStatus,
  DesignDocumentStatus,
  DimensionType,
  PermissionType,
  PermissionEntityType,
  PermissionResourceType,
  InspectionStatus,
  IssuePriority,
  QuickQuoteStatus,
  IssueStatus,
  LogSeverityLevel,
  MeasurementUnit,
  OrganizationType,
  TeamType,
  AccountInvitationStatus,
  BillingProvider,
  ProposalStatus,
  ResourceType,
  SolutionTenderingType,
  SolutionStatus,
  UserOrganizationAssociationType,
  UserOrganizationRole,
  WorkTypeAssociationScopeEntity,
  TenderStatus,
  TemplateType,
  TemplateEntityType,
  SolutionOperationType,
  ResponseStatus,
  CompleteEventStatus,
  LinkAction,
  MeetingStatus,
  AttendeeRole,
  BuildStatus,
  NotificationResourceType,
  NotificationType,
  NotificationRecipientIdType,
  RecurrenceType,
  DayOfWeek,
  MonthlyScheduleType,
  AssessmentStatus,
  ExternalReportType,
  ImageSource,
  GenerateReportResourceType,
  S3DownloadLocation,
  NotificationChannel,
  AccountClaimStatus,
  MeetingLinkType,
  AssessmentCompletionStatus,
  AttachmentLocation,
  AccountTier,
  ConversationStatus,
  ConversationNextStep,
  GenerateIssuesFromImagesJobStatus,
  BidMetadata,
  ProposalDocLink,
  SolutionMetadata,
  Measurement,
  TenderMetadata,
  CreateTemporaryAuthorizationTokenResponse,
  ListResourceAggregateUserPermissionsOutput,
  AggregateUserPermissions,
  CompleteEvent,
  PlainFormConfiguration,
  AssessmentFormConfiguration,
  IssueSubmissionFormConfiguration,
  NotificationRecipient,
  Recurrence,
  DailyRecurrence,
  WeeklyRecurrence,
  MonthlyRecurrence,
  NthWeekDay,
  ExternalUserInfo,
  GetAccountClaimStatusResponse,
  ChimeMeetingOutput,
  SearchUsersOutput,
  SearchOrganizationsOutput,
  SearchableContactFilterOutput,
  ListFormConfigurationsByPropertyOutput,
  AssessmentScheduleByAssessors,
  ListAssessmentSchedulesByAssessorsOutput,
  SummarizeIssueOutput,
  FilterImagesOutput,
  WorkTypeDTO,
  SubmitQuickQuoteOutput,
  CreateBillingProfileSessionResponse,
  CreateBillingProfileResponse,
  SubscriptionDetails,
  ReportMeteringUsageResponse,
  UpdatesubscriptionTierResponse,
  ConversationStateOutput,
  ClassifyIssuesOutput,
  ClassifiedIssue
};